import * as React from 'react';
import { Button, Modal } from '@teamsnap/teamsnap-ui';
import { DateService, DateTimePicker } from 'frontend-toolkit';

import CalendarExportButton from './calendar-export-button';
import { Filters } from './calendar-utils';

type Props = {
  title: string;
  show: boolean;
  activeFilters: Filters;
  organizationId: string;
  onClose: () => any;
};

const CalendarExportModal = ({ title, show, activeFilters, organizationId, onClose }: Props) => {
  const [startDate, setStartDate] = React.useState<Date>(null);
  const [endDate, setEndDate] = React.useState<Date>(null);
  const [dateError, setDateError] = React.useState<string>(undefined);

  const validateDates = (startDateInput, endDateInput) => {
    if (!startDateInput || !endDateInput) return false;

    const startDateGreaterThanEndDate = DateService.compareDates(
      new Date(startDateInput),
      new Date(endDateInput),
      1,
      'greater_or_equal'
    );

    const timeDifferenceInMilliseconds = Math.abs(startDateInput.getTime() - endDateInput.getTime());
    const daysDifference = timeDifferenceInMilliseconds / (1000 * 60 * 60 * 24);
    const dateRangeGreaterThan90Days = daysDifference > 90;

    if (!startDateInput || !endDateInput) {
      setDateError('Date range is required.');
      return false;
    } else if (startDateGreaterThanEndDate) {
      setDateError('End date cannot be before the start date.');
      return false;
    } else if (dateRangeGreaterThan90Days) {
      setDateError('Date range cannot exceed 90 days.');
      return false;
    } else {
      setDateError(undefined);
      return true;
    }
  };

  const disableExportButton = !startDate || !endDate || !!dateError;

  const anyActiveFilters = Object.values(activeFilters).some((filter) => filter.length > 0);

  const resetExportModal = () => {
    setDateError(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
  };

  return (
    <>
      <Modal
        show={show}
        allowOverlayClose
        style={{ minWidth: '0', width: '451px', textAlign: 'start', height: '75vh' }}
        closeFn={() => {
          resetExportModal();
          onClose();
        }}
        heading={title}
        showClose
      >
        {anyActiveFilters && <div className="u-padBottomMd u-fontSizeMd">Filters selected on the calendar apply</div>}
        <div id="export-modal-content" className="u-padBottomLg">
          <div className="u-padBottomSm u-fontSizeLg">
            Date range<span className="u-colorNegative4">*</span>
          </div>
          <DateTimePicker
            datetime={startDate}
            dateFormat="P"
            onChange={(date) => {
              setStartDate(date);
              validateDates(date, endDate);
            }}
            placeholderText="Start Date"
            showTimeSelect={false}
            containerClasses={`u-padBottomSm sui-px-1 ${dateError ? 'sui-border-red-4' : 'u-padBottomSm'}`}
          />
          <DateTimePicker
            datetime={endDate}
            dateFormat="P"
            onChange={(date) => {
              setEndDate(date);
              validateDates(startDate, date);
            }}
            placeholderText="End Date"
            showTimeSelect={false}
            containerClasses={`u-padBottomSm u-spaceTopMd sui-px-1 ${dateError ? 'sui-border-red-4' : 'u-padBottomSm'}`}
          />
        </div>
        <div className={dateError ? 'u-padXs u-spaceEndsXs validation-error' : 'u-padXs u-spaceEndsXs'}>
          {dateError && <p className="validation-error">{dateError}</p>}
        </div>
        <div className="export-modal-buttons">
          <Button
            mods="u-spaceRightSm"
            onClick={() => {
              resetExportModal();
              onClose();
            }}
          >
            Cancel
          </Button>
          <CalendarExportButton
            title="Export csv"
            isDisabled={disableExportButton}
            startDate={startDate}
            endDate={endDate}
            organizationId={organizationId}
            activeFilters={activeFilters}
            onClose={() => {
              resetExportModal();
              onClose();
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default CalendarExportModal;
